import { CircleAlert, CloudOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from '../../lib/cx';
import { useT } from '../../lib/i18n';
import { commonMessages } from '../../lib/i18n/messages/common';
import { useOnline } from '../../lib/online';
import { Button } from './Button';

/** Sticky strip shown while offline (DESIGN 3.4). */
export function OfflineBanner() {
  const online = useOnline();
  const t = useT(commonMessages);
  if (online) return null;
  return (
    <div
      role="status"
      className="sticky top-0 z-30 flex items-start gap-2 bg-warn-soft px-5 py-2.5 pt-[max(0.625rem,var(--safe-top))] text-sm font-medium text-warn"
    >
      <CloudOff className="ic ic-sm mt-px" strokeWidth={1.75} aria-hidden />
      <span>{t('offlineBanner')}</span>
    </div>
  );
}

/** Inline `warn-soft` note. */
export function Callout({
  children,
  tone = 'warn',
  className,
  role,
}: {
  children: ReactNode;
  tone?: 'warn' | 'owe' | 'neutral';
  className?: string;
  role?: 'alert' | 'status';
}) {
  return (
    <div
      role={role}
      className={cx(
        'rounded-control px-4 py-3 text-sm leading-5 font-medium',
        tone === 'warn' && 'bg-warn-soft text-warn',
        tone === 'owe' && 'bg-owe-soft text-owe',
        tone === 'neutral' && 'bg-sunken text-ink',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Inline error card with "Try again" (DESIGN 3.4 Error). */
export function ErrorCard({
  message,
  onRetry,
  className,
}: {
  message?: ReactNode;
  onRetry?: () => void;
  className?: string;
}) {
  const t = useT(commonMessages);
  return (
    <div role="alert" className={cx('card flex flex-col items-start gap-3 p-4', className)}>
      <p className="flex items-start gap-2 text-ink">
        <CircleAlert className="ic mt-px text-owe" strokeWidth={1.75} aria-hidden />
        <span>{message ?? t('genericError')}</span>
      </p>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          {t('tryAgain')}
        </Button>
      )}
    </div>
  );
}
